import { useMemoizedFn, useMount, useUpdateLayoutEffect } from 'ahooks';
import { Empty, Skeleton } from 'antd';
import { useAppDispatch } from 'app/hooks';
import { Paged } from 'domains/appDomain';
import { GallerySku } from 'domains/gallerySkuDomain';
import { SkuType } from 'domains/skuDomain';
import { uniqBy } from 'lodash-es';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

interface Props<T> {
  className?: string;
  loadMore: (pageNumber: number) => Promise<Paged<T>>;
  render: ({ items }: { items?: T[] }) => JSX.Element;
  selectedSkuType: SkuType | null;
  /**
   * Called once the first page settles, with the size of the whole result rather than of the
   * page, and with that page's items. The host owns what they mean: the Canva match view puts
   * the number on the category heading and treats zero as "no product fits this design", and
   * the category picker uses a result of one to publish without asking.
   */
  onTotalChange?: (total: number, items: T[]) => void;
  /**
   * Height the list reserves while it has nothing to show yet, so the loader is not laid over a
   * collapsed box. The Canva match view passes 0: a set of matches is usually a single row, and
   * the reserve would leave a blank band between the cards and the link that follows them.
   */
  minHeight?: number;
}

export const CompanyCardInfiniteList = ({
  className,
  loadMore,
  render,
  selectedSkuType,
  onTotalChange,
  minHeight = 600,
}: Props<GallerySku>) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [items, setItems] = useState<GallerySku[]>();
  const scrollRef = useRef<any>(null);
  const totalPagesRef = useRef<number>(1);
  /**
   * Bumped by every reset. A request started before the reset belongs to the category the user has
   * left, so its response is dropped instead of being merged into the new one - otherwise the page
   * that was already in flight when the category changed shows up alongside the new category.
   */
  const loadGenerationRef = useRef(0);
  /** Reported at most once per load cycle, so a re-render cannot send the host round again. */
  const totalReportedRef = useRef(false);

  const loadItems = useCallback(
    async (pageNumber: number) => {
      if (pageNumber < totalPagesRef.current + 1) {
        const generation = loadGenerationRef.current;
        const isCurrent = () => generation === loadGenerationRef.current;
        try {
          setLoading(true);
          const result = await loadMore(pageNumber);
          if (!isCurrent()) {
            return;
          }
          setTotalItems(result.totalElements);
          totalPagesRef.current = result.totalPages;
          // Only on a settled first page: later pages carry the same total, and re-reporting
          // would have the host react again to something that has not changed.
          if (!totalReportedRef.current) {
            totalReportedRef.current = true;
            onTotalChange?.(result.totalElements, result.content || []);
          }
          setItems((pre) =>
            uniqBy([...(pre || []), ...(result.content || [])], 'id')
          );
        } catch (e) {
          if (isCurrent()) {
            setItems([]);
          }
        } finally {
          if (isCurrent()) {
            setLoading(false);
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadMore, onTotalChange, totalPagesRef.current]
  );

  const reset = useMemoizedFn(() => {
    loadGenerationRef.current += 1;
    totalReportedRef.current = false;
    setLoading(false);
    setTotalItems(0);
    totalPagesRef.current = 1;
    setItems([]);
    if (scrollRef.current) {
      scrollRef.current.pageLoaded = 1;
    }
    loadItems(0);
  });

  useUpdateLayoutEffect(() => {
    reset();
  }, [selectedSkuType]);

  useMount(() => {
    dispatch.workspace.setState({
      resetJobList: reset,
      // updateJobList: updateJobItem,
    });
    // Fetch the first page here rather than leaving it to the scroller. InfiniteScroll only calls
    // loadMore once its sentinel enters the viewport, which depends on the height of whatever
    // page is hosting this list -- on a layout where the sentinel starts out of view the list
    // never loads at all and renders as empty. Asking for page one on mount does not depend on
    // the host's layout; the scroller still drives every page after it, and uniqBy keeps a
    // duplicate page-one response from doubling the grid.
    loadItems(0);
  });

  const renderDom = useMemo(() => {
    return React.createElement(render, { items });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  return (
    <div className={className} style={{ minHeight, overflow: 'auto' }}>
      <InfiniteScroll
        ref={scrollRef}
        loadMore={loadItems}
        hasMore={(!items || totalItems > items.length) && !loading}
        threshold={200}
      >
        {renderDom}
      </InfiniteScroll>
      {Boolean((!items || !items?.length) && !loading) && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 30 }} />
      )}
      {loading && (
        <div className="workspace-item">
          <Skeleton active />
        </div>
      )}
    </div>
  );
};
